// Formatação em português do Brasil, fuso America/Fortaleza, moeda BRL.

export const TZ = 'America/Fortaleza';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

// Valores chegam como string decimal ("1600.00"); a conversão para número aqui é só para exibição.
export const money = (value: string | null | undefined) => (value == null ? '—' : brl.format(Number(value)));

// 90 → "1h30"; 1200 → "20h00"
export function hm(minutes: number | null | undefined): string {
  if (minutes == null) return '—';
  const sign = minutes < 0 ? '−' : '';
  const abs = Math.abs(minutes);
  return `${sign}${Math.floor(abs / 60)}h${String(abs % 60).padStart(2, '0')}`;
}

// "2026-10-03" → "03/10/2026" (data pura, sem fuso)
export function date(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = value.slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}

// Data de hoje em America/Fortaleza no formato AAAA-MM-DD (toISOString usaria UTC e viraria o dia às 21h).
export const todayIso = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());

export function dateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: TZ, dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

export function documentMask(type: 'cpf' | 'cnpj' | null, digits: string | null): string {
  if (!digits) return '—';
  if (type === 'cpf') return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  if (type === 'cnpj') return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  return digits;
}

export function phoneMask(digits: string | null): string {
  if (!digits) return '—';
  const d = digits.replace(/^55(?=\d{10,11}$)/, '');
  if (d.length === 11) return d.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  return digits;
}

// Entrada "1.500,00" ou "1500.00" → "1500.00" (formato aceito pela API). Retorna null se inválido.
export function parseMoneyInput(input: string): string | null {
  const s = input.trim().replace(/^R\$\s*/, '');
  if (!s) return null;
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  return /^\d{1,12}(\.\d{1,2})?$/.test(normalized) ? normalized : null;
}

// "20" → 1200; "1:30" ou "1h30" → 90; "1,5" → 90
export function parseHoursInput(input: string): number | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;
  const hm = /^(\d+)\s*[h:]\s*(\d{1,2})?$/.exec(s);
  if (hm) {
    const mins = Number(hm[2] ?? 0);
    return mins < 60 ? Number(hm[1]) * 60 + mins : null;
  }
  const dec = /^(\d+)(?:[.,](\d+))?$/.exec(s);
  if (dec) return Math.round(Number(`${dec[1]}.${dec[2] ?? 0}`) * 60);
  return null;
}

export const MODALITY_LABEL = {
  avulso: 'Hora avulsa',
  banco_horas: 'Banco de horas',
  valor_fechado: 'Valor fechado',
  mensalidade: 'Mensalidade',
} as const;

export const PROJECT_STATUS_LABEL = { ativo: 'Ativo', pausado: 'Pausado', encerrado: 'Encerrado', cancelado: 'Cancelado' } as const;

export const PURPOSE_LABEL = { agenda: 'Agenda', relatorio: 'Relatórios', extrato: 'Extrato', cobranca: 'Cobrança' } as const;

// ------------------------------------------------------------------ fuso (P2)

const partsFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

// Instante ISO → data e hora locais (America/Fortaleza).
export function localParts(iso: string): { date: string; time: string } {
  const p = Object.fromEntries(partsFmt.formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

export const timeOf = (iso: string | null | undefined) => (iso ? localParts(iso).time : '—');

function offsetMinutes(instant: Date): number {
  const p = Object.fromEntries(partsFmt.formatToParts(instant).map((x) => [x.type, x.value]));
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute));
  return Math.round((asUtc - Math.floor(instant.getTime() / 60000) * 60000) / 60000);
}

// Data + hora locais → instante ISO (UTC).
export function zonedIso(day: string, time: string): string {
  const guess = new Date(`${day}T${time}:00Z`);
  return new Date(guess.getTime() - offsetMinutes(guess) * 60000).toISOString();
}

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const weekdayShort = (day: string) => new Intl.DateTimeFormat('pt-BR', { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const longDate = (day: string) =>
  cap(new Intl.DateTimeFormat('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`)));
export const monthLabel = (day: string) => cap(new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`)));

export const APPOINTMENT_STATUS_LABEL = {
  agendado: 'Agendado',
  confirmado: 'Confirmado',
  realizado: 'Realizado',
  cancelado: 'Cancelado',
  reagendado: 'Reagendado',
  nao_compareceu: 'Não compareceu',
} as const;

export const EXECUTION_STATUS_LABEL = { rascunho: 'Rascunho', pronto: 'Pronto para conferência', confirmado: 'Confirmado', anulado: 'Anulado' } as const;
export const SERVICE_MODALITY_LABEL = { remoto: 'Remoto', presencial: 'Presencial' } as const;
